import { Text, Flex, Icon, Button } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { downloadBlob } from '@/shared/utils/file-utils';

export interface IUnsupportedFilePreviewProps {
  /** The file to offer for download. */
  blob: Blob;
  /** Name used for the download and shown to the user. */
  fileName: string;
  /**
   * Optional human-readable explanation of why the preview is unavailable.
   * When omitted, a generic "unable to render" message is shown.
   */
  reason?: string;
}

/**
 * Fallback shown for files we can't preview in-browser (unknown types, detected
 * legacy/binary formats, or files a renderer failed to parse). Offers a direct
 * download instead of surfacing a technical error, and can display a
 * human-readable reason for the missing preview.
 */
export function UnsupportedFilePreview({
  blob,
  fileName,
  reason,
}: IUnsupportedFilePreviewProps) {
  const { t } = useTranslation('file-renderer');
  const { t: tCommon } = useTranslation('common');

  return (
    <Flex direction="column" gap={4} align="center" padding={8}>
      <Icon type="File" size="sm" className="text-muted-foreground" />
      <Flex direction="column" gap={2} align="center">
        <Text weight="medium">
          {t('fileRenderer.error.unsupportedFileType')}
        </Text>
        <Text level="small" color="muted" className="text-center">
          {reason ?? t('fileRenderer.error.unableToRender')}
        </Text>
        <Text level="small" color="muted">
          {t('fileRenderer.error.file', { fileName })}
        </Text>
      </Flex>
      <Button variant="secondary" onClick={() => downloadBlob(blob, fileName)}>
        <Icon type="Download" />
        {tCommon('common.action.download')}
      </Button>
    </Flex>
  );
}
